import moment from 'moment';

/**
 * Single source of truth for pickup/sold date-range rules. Both `itemDatesValidator`
 * (form validation) and the edit-item datepicker bounds derive their behavior from the
 * helpers here, so the two can never drift apart.
 *
 * The calendar only enforces start/end ordering (an end cannot precede its start, and a
 * start cannot follow its end). The 120-day range width and backdating limits are left to
 * the validator, which surfaces an explanatory error rather than silently disabling dates.
 */

/** Maximum allowed span (in days) for a date range, and the furthest a start date may be backdated. */
export const MAX_DATE_RANGE_DAYS = 120;

/** Parses an arbitrary form-control value into a valid moment, or null when empty/invalid. */
export function parseDate(value: unknown): moment.Moment | null {
  if (!value) {
    return null;
  }
  const parsed = moment(value as moment.MomentInput);
  return parsed.isValid() ? parsed : null;
}

/** Earliest date a range may start: today less the maximum backdating window. */
export function earliestAllowedStartDate(): moment.Moment {
  return moment().startOf('day').subtract(MAX_DATE_RANGE_DAYS, 'days');
}

/** Whole-day span between two dates. Uses moment's day diff, which is DST-safe. */
export function daysBetween(start: moment.Moment, end: moment.Moment): number {
  return end.diff(start, 'days');
}

/** Latest selectable start date given the currently chosen end date (start cannot follow end). */
export function maxStartDate(endValue: unknown): Date | null {
  return parseDate(endValue)?.toDate() ?? null;
}

/** Earliest selectable end date given the currently chosen start date (end cannot precede start). */
export function minEndDate(startValue: unknown): Date | null {
  return parseDate(startValue)?.toDate() ?? null;
}
